import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Calendar, Users, BarChart3, Music, Building2, FileText, DollarSign, CheckCircle2,
  ArrowRight, Menu, X, Heart, Shield, Globe, Sparkles, Bot, Plug, Calculator,
  Mail, CreditCard, Ticket, Store, Star, TrendingUp, Play, Quote, User,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { LanguageToggle } from "@/components/landing/LanguageToggle";
import { useLandingLanguage } from "@/hooks/useLandingLanguage";
import { useHeroImage } from "@/hooks/useLandingImages";
import { useDemoVideoSettings } from "@/hooks/useLandingSettings";
import { useLandingTracking } from "@/hooks/useLandingTracking";
import { PublicFooter } from "@/components/layout/PublicFooter";

import dashboardImg from "@/assets/screenshots/dashboard.jpg";
import concertsImg from "@/assets/screenshots/concerts.jpg";
import calendarImg from "@/assets/screenshots/calendar.jpg";
import revenueSharesImg from "@/assets/screenshots/revenue-shares.jpg";
import analyticsImg from "@/assets/screenshots/analytics.jpg";
import contactsImg from "@/assets/screenshots/contacts.jpg";

const LandingContent = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, lang } = useLandingLanguage();
  const { heroImage, isLoading: heroImageLoading } = useHeroImage();
  const { data: demoVideoSettings } = useDemoVideoSettings();
  const { trackCTA, trackFAQOpen, trackNewsletterSubmit } = useLandingTracking();

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const features = [
    { title: t.features.dashboard.title, description: t.features.dashboard.description, icon: BarChart3, image: dashboardImg },
    { title: t.features.concerts.title, description: t.features.concerts.description, icon: Music, image: concertsImg },
    { title: t.features.calendar.title, description: t.features.calendar.description, icon: Calendar, image: calendarImg },
    { title: t.features.revenue.title, description: t.features.revenue.description, icon: DollarSign, image: revenueSharesImg },
    { title: t.features.analytics.title, description: t.features.analytics.description, icon: TrendingUp, image: analyticsImg },
    { title: t.features.contacts.title, description: t.features.contacts.description, icon: Users, image: contactsImg },
  ];

  const advancedFeatures = [
    { icon: Heart, title: t.advancedFeatures.fanCrm.title, description: t.advancedFeatures.fanCrm.description, color: "text-pink-500", bgColor: "bg-pink-500/10" },
    { icon: Bot, title: t.advancedFeatures.ai.title, description: t.advancedFeatures.ai.description, color: "text-amber-500", bgColor: "bg-amber-500/10" },
    { icon: Calculator, title: t.advancedFeatures.calculator.title, description: t.advancedFeatures.calculator.description, color: "text-green-500", bgColor: "bg-green-500/10" },
    { icon: FileText, title: t.advancedFeatures.contracts.title, description: t.advancedFeatures.contracts.description, color: "text-blue-500", bgColor: "bg-blue-500/10" },
  ];

  const integrations = [
    { name: t.integrations.ticketTailor.name, icon: Ticket, description: t.integrations.ticketTailor.description },
    { name: t.integrations.mailerLite.name, icon: Mail, description: t.integrations.mailerLite.description },
    { name: t.integrations.paypal.name, icon: CreditCard, description: t.integrations.paypal.description },
    { name: t.integrations.woocommerce.name, icon: Store, description: t.integrations.woocommerce.description },
  ];

  const pricingPlans = [
    { name: t.pricing.free.name, price: "0", description: t.pricing.free.description, features: t.pricing.free.features, popular: false },
    { name: t.pricing.artistPro.name, price: "19", description: t.pricing.artistPro.description, features: t.pricing.artistPro.features, popular: true },
    { name: t.pricing.manager.name, price: "49", description: t.pricing.manager.description, features: t.pricing.manager.features, popular: false },
  ];

  const faqs = t.faq.items;

  const stats = [
    { value: "50+", label: t.stats.concerts },
    { value: "2.800+", label: t.stats.fans },
    { value: "100%", label: t.stats.gdpr },
    { value: "🇮🇹", label: t.stats.madeIn },
  ];

  const heroImageUrl = heroImage?.image_url || dashboardImg;

  return (
    <div className="min-h-dvh min-w-0 overflow-x-clip bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/10">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-2 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <Music className="h-8 w-8 text-primary" />
            <span className="font-bold text-xl">Maestro CRM</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection("features")} className="text-muted-foreground hover:text-foreground transition-colors">{t.nav.features}</button>
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">{t.nav.pricing}</Link>
            <button onClick={() => scrollToSection("integrations")} className="text-muted-foreground hover:text-foreground transition-colors">{t.nav.integrations}</button>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <LanguageToggle />
            <Button variant="ghost" onClick={() => { trackCTA("start_free", "nav"); navigate("/auth"); }}>{t.nav.login}</Button>
            <Button onClick={() => { trackCTA("start_free", "nav"); navigate("/auth"); }}>{t.nav.startFree}</Button>
          </div>
          <div className="flex md:hidden items-center gap-2">
            <LanguageToggle />
            <button className="inline-flex h-11 w-11 items-center justify-center" aria-label={mobileMenuOpen ? "Chiudi menu" : "Apri menu"} aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t bg-background px-4 py-3 md:hidden">
            <div className="mx-auto flex max-w-7xl flex-col gap-1">
            <button onClick={() => scrollToSection("features")} className="min-h-11 text-left">{t.nav.features}</button>
            <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="flex min-h-11 items-center">{t.nav.pricing}</Link>
            <button onClick={() => scrollToSection("integrations")} className="min-h-11 text-left">{t.nav.integrations}</button>
            <div className="flex flex-col gap-3 pt-3 border-t">
              <Button 
                variant="outline" 
                onClick={() => { trackCTA("login", "nav_mobile"); navigate("/auth"); setMobileMenuOpen(false); }} 
                className="w-full min-h-[44px]"
              >
                {t.nav.login}
              </Button>
              <Button 
                onClick={() => { trackCTA("start_free", "nav_mobile"); navigate("/auth?tab=signup"); setMobileMenuOpen(false); }} 
                className="w-full min-h-[44px]"
              >
                {t.nav.startFree}
              </Button>
            </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero — Gradient Background */}
      <section className="relative overflow-hidden px-4 pb-16 pt-24 sm:pb-20 sm:pt-28 lg:pb-24 lg:pt-32 bg-gradient-to-br from-primary via-primary/90 to-accent">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="heroPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="currentColor" />
                <circle cx="25" cy="25" r="1.5" fill="currentColor" />
                <circle cx="75" cy="75" r="1.5" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#heroPattern)" />
          </svg>
        </div>
        <div className="absolute top-20 left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-accent/20 rounded-full blur-2xl" />

        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left">
              <Badge className="mb-4 max-w-full whitespace-normal bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/30">
                <Sparkles className="h-3 w-3 mr-1" />
                {t.hero.badge}
              </Badge>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-primary-foreground leading-tight">
                {t.hero.headline}
              </h1>
              <p className="text-lg sm:text-xl text-primary-foreground/80 mb-8 max-w-xl mx-auto lg:mx-0">
                {t.hero.subheadline}
              </p>
              
              {/* Double CTA */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
                 <Button size="lg" onClick={() => { trackCTA("start_free", "hero"); navigate("/auth"); }} className="w-full gap-2 px-6 text-base sm:w-auto sm:px-8 bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                  {t.hero.ctaPrimary}
                  <ArrowRight className="h-4 w-4" />
                </Button>
                 <Button size="lg" variant="outline" onClick={() => { trackCTA("watch_demo", "hero"); scrollToSection("features"); }} className="w-full gap-2 text-base sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                  <Play className="h-4 w-4 fill-current" />
                  {t.hero.ctaSecondary}
                </Button>
              </div>

              {/* Trust Badge */}
              <p className="text-sm text-primary-foreground/90">
                {t.hero.trustBadge}
              </p>
            </div>

            {/* Right: Hero Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20">
                {!heroImageLoading && (
                  <img
                    src={heroImageUrl}
                    alt={heroImage?.alt_text || "Maestro CRM Dashboard"}
                    className="w-full h-auto"
                    width={1200}
                    height={750}
                    fetchPriority="high"
                    decoding="async"
                  />
                )}
                {heroImageLoading && (
                  <div className="aspect-video bg-white/10 animate-pulse" />
                )}
              </div>
            </div>
          </div>

          {/* Stats Bar — glassmorphism inside hero */}
           <div className="mt-12 grid grid-cols-2 gap-3 sm:mt-16 sm:gap-4 md:grid-cols-4 max-w-3xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="min-w-0 text-center bg-primary-foreground/10 backdrop-blur-sm rounded-xl px-2 py-4 sm:px-4">
                <div className="text-2xl md:text-3xl font-bold text-primary-foreground mb-1">{stat.value}</div>
                <div className="break-words text-xs md:text-sm text-primary-foreground/70">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="text-base px-4 py-2 border-primary/30 text-primary bg-primary/5">
              {t.testimonials.badge}
            </Badge>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {t.testimonials.items.map((testimonial, idx) => (
              <Card key={idx} className="relative hover:shadow-lg transition-all hover:-translate-y-1 border-border/50">
                <CardHeader className="pb-2">
                  <Quote className="h-8 w-8 text-primary/20 absolute top-4 right-4" />
                  <p className="text-lg font-medium leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-sm">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-center">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <span className="font-semibold">{t.testimonials.stats.revenue}</span>
            </div>
            <div className="hidden md:block text-muted-foreground">|</div>
            <div className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-primary" />
              <span className="font-semibold">{t.testimonials.stats.concerts}</span>
            </div>
            <div className="hidden md:block text-muted-foreground">|</div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <span className="font-semibold">{t.testimonials.stats.users}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">{t.features.title}</h2>
          <p className="text-lg text-muted-foreground text-center mb-16 max-w-2xl mx-auto">{t.features.subtitle}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <Card key={i} className="overflow-hidden group hover:shadow-xl transition-all hover:-translate-y-1 border-border/50">
                <div className="aspect-video overflow-hidden bg-muted">
                  <img src={f.image} alt={f.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <CardHeader><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-primary/10"><f.icon className="h-5 w-5 text-primary" /></div><CardTitle className="text-lg">{f.title}</CardTitle></div></CardHeader>
                <CardContent><CardDescription className="leading-relaxed">{f.description}</CardDescription></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto">
          <Badge variant="outline" className="mb-4 mx-auto block w-fit">{t.advancedFeatures.badge}</Badge>
          <h2 className="text-3xl font-bold text-center mb-16">{t.advancedFeatures.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advancedFeatures.map((f, i) => (
              <Card key={i} className="text-center hover:shadow-xl transition-all hover:-translate-y-1 border-border/50">
                <CardHeader><div className={`w-16 h-16 rounded-2xl ${f.bgColor} flex items-center justify-center mx-auto mb-4`}><f.icon className={`h-8 w-8 ${f.color}`} /></div><CardTitle className="text-lg">{f.title}</CardTitle></CardHeader>
                <CardContent><CardDescription>{f.description}</CardDescription></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <Badge variant="outline" className="text-base px-4 py-2 mb-4 border-primary/30 text-primary bg-primary/5">
              {t.demo.badge}
            </Badge>
            <p className="text-xl text-muted-foreground">
              {t.demo.subtitle}
            </p>
          </div>

          <div className="mb-8">
            {demoVideoSettings?.enabled && demoVideoSettings?.url ? (
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border bg-muted">
                <iframe
                  src={demoVideoSettings.url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="MaestroCRM Demo"
                />
              </div>
            ) : (
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border bg-muted flex items-center justify-center">
                <div className="text-center">
                  <Play className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">{t.demo.comingSoon}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mb-8">
            {t.demo.highlights.map((highlight, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium">{highlight}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button size="lg" onClick={() => { trackCTA("start_free", "demo"); navigate("/auth"); }} className="gap-2 text-base px-8">
              {t.demo.cta}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-4 bg-muted/20">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">{t.pricing.title}</h2>
          <p className="text-lg text-muted-foreground text-center mb-16">{t.pricing.subtitle}</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <Card key={i} className={`relative flex flex-col transition-all hover:-translate-y-1 ${plan.popular ? "border-primary shadow-xl md:scale-[1.03]" : "border-border/50 hover:shadow-lg"}`}>
                {plan.popular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">{t.pricing.mostPopular}</Badge>}
                <CardHeader className="text-center">
                  <CardTitle>{plan.name}</CardTitle>
                  <div className="mt-4"><span className="text-4xl font-bold">€{plan.price}</span><span className="text-muted-foreground">{t.pricing.perMonth}</span></div>
                  <CardDescription className="mt-2">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <ul className="space-y-3 flex-1">{plan.features.map((f, j) => <li key={j} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /><span className="text-sm">{f}</span></li>)}</ul>
                  <Button 
                    className="w-full mt-6" 
                    variant={plan.popular ? "default" : "outline"}
                    onClick={() => { trackCTA(plan.price === "0" ? "start_free" : "choose_plan", "pricing_card"); navigate("/auth"); }}
                  >
                    {plan.price === "0" ? t.pricing.startFree : t.pricing.choosePlan}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12"><Link to="/pricing" onClick={() => trackCTA("view_pricing", "pricing")}><Button variant="outline" size="lg" className="gap-2">{t.pricing.viewAll}<ArrowRight className="h-4 w-4" /></Button></Link></div>
        </div>
      </section>

      {/* Integrations */}
      <section id="integrations" className="py-20 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4"><Plug className="h-6 w-6 text-primary" /><Badge variant="outline">{t.integrations.badge}</Badge></div>
          <h2 className="text-3xl font-bold mb-16">{t.integrations.title}</h2>
          <div className="grid md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {integrations.map((integration, idx) => (
              <Card key={idx} className="text-center hover:shadow-lg transition-all hover:-translate-y-1 border-border/50">
                <CardHeader><div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3"><integration.icon className="h-7 w-7 text-primary" /></div><CardTitle className="text-base">{integration.name}</CardTitle></CardHeader>
                <CardContent><CardDescription className="text-sm">{integration.description}</CardDescription></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-muted/20">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-16">{t.faq.title}</h2>
          <Accordion 
            type="single" 
            collapsible 
            className="w-full"
            onValueChange={(value) => {
              if (value) {
                const index = parseInt(value.replace("item-", ""));
                trackFAQOpen(index);
              }
            }}
          >
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA — Gradient */}
      <section className="relative py-24 px-4 bg-gradient-to-r from-primary to-accent overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="ctaPattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                <circle cx="40" cy="40" r="1.5" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ctaPattern)" />
          </svg>
        </div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto text-center max-w-2xl relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">{t.cta.title}</h2>
          <p className="text-lg text-white/80 mb-8">{t.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => { trackCTA("start_free", "cta_section"); navigate("/auth"); }} className="w-full gap-2 sm:w-auto bg-primary-foreground text-primary hover:bg-primary-foreground/90">
              {t.cta.primary}
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Link className="w-full sm:w-auto" to="/pricing" onClick={() => trackCTA("view_pricing", "cta_section")}>
              <Button size="lg" variant="outline" className="w-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                {t.cta.secondary}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

const Landing = () => <LandingContent />;

export default Landing;
